import React from "react";
import {
  TextField,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  Box,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxCheckedIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxIcon fontSize="small" />;
const checkedIcon = <CheckBoxCheckedIcon fontSize="small" />;

export const InputField = React.memo(
  ({
    label,
    name,
    value,
    handleChange,
    placeholder,
    multiline = false,
    rows = 1,
    gridSizes = { lg: 4, md: 6, sm: 6, xs: 12 },
    required = false,
  }) => (
    <Grid item {...gridSizes}>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          {label}
          {required && <span style={{ color: "red" }}>*</span>}{" "}
        </Typography>
      )}

      <TextField
        fullWidth
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        size="small"
      />
    </Grid>
  )
);

export const MultiSelectDropdown = React.memo(
  ({
    label,
    name,
    value,
    handleChange,
    options = [],
    placeholder,
    gridSizes = { lg: 4, md: 6, sm: 6, xs: 12 },
    required = false,
  }) => (
    <Grid item {...gridSizes}>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          {label}
          {required && <span style={{ color: "red" }}>*</span>}{" "}
        </Typography>
      )}
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={Array.isArray(value) ? value : []}
        onChange={(event, newValue) =>
          handleChange({ target: { name, value: newValue } })
        }
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} size="small" />
        )}
      />
    </Grid>
  )
);

export const CheckboxField = React.memo(
  ({
    label,
    name,
    checked,
    handleChange,
    gridSizes = { lg: 4, md: 6, sm: 6, xs: 12 },
    required = false,
  }) => (
    <Grid item {...gridSizes}>
      <FormControlLabel
        control={
          <Checkbox name={name} checked={checked} onChange={handleChange} />
        }
        label={
          <>
            {label}
            {required && <span style={{ color: "red" }}>*</span>}{" "}
          </>
        }
      />
    </Grid>
  )
);

export const Dropdown = React.memo(
  ({
    label,
    name,
    value,
    options = [],
    handleChange,
    placeholder,
    gridSizes = { lg: 4, md: 6, sm: 6, xs: 12 },
    disableOptionCondition = () => false,
    disabled = false,
    required = false,
  }) => (
    <Grid item {...gridSizes}>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          {label}
          {required && <span style={{ color: "red" }}>*</span>}{" "}
        </Typography>
      )}
      <FormControl
        fullWidth
        size="small"
        variant="outlined"
        disabled={disabled}
      >
        <Select
          name={name}
          value={value}
          displayEmpty
          onChange={handleChange}
          sx={{ backgroundColor: "#fff", borderRadius: 1 }}
          renderValue={(selected) => {
            if (selected === undefined || selected === null) return placeholder;
            const selectedOption = options.find((opt) =>
              typeof opt === "object" ? opt.id === selected : opt === selected
            );
            return typeof selectedOption === "object"
              ? selectedOption.name
              : selectedOption ?? placeholder;
          }}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
          disabled={disabled}
        >
          <MenuItem value="">
            <em>{placeholder}</em>
          </MenuItem>
          {options.map((opt, index) => {
            const optionLabel = typeof opt === "object" ? opt.name : opt;
            const optionValue = typeof opt === "object" ? opt.id : opt;
            const isDisabled = disableOptionCondition(opt);

            return (
              <MenuItem
                key={optionValue ?? index}
                value={optionValue}
                disabled={isDisabled}
              >
                {optionLabel}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Grid>
  )
);

export const AutocompleteWithSearch = React.memo(
  ({
    label,
    name,
    value,
    onChange,
    options = [],
    placeholder,
    gridSizes = { lg: 4, md: 6, sm: 6, xs: 12 },
    required = false,
  }) => {
    const selectedOption = options.find((opt) => opt.id === value) || null;

    return (
      <Grid item {...gridSizes}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {label && (
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              {label}
              {required && <span style={{ color: "red" }}>*</span>}{" "}
            </Typography>
          )}
          <FormControl fullWidth size="small">
            <Autocomplete
              options={options}
              getOptionLabel={(option) => option.name}
              value={selectedOption}
              onChange={(event, newValue) =>
                onChange({ target: { name, value: newValue?.id || "" } })
              }
              isOptionEqualToValue={(option, val) => option.id === val?.id}
              renderInput={(params) => (
                <TextField {...params} placeholder={placeholder} size="small" />
              )}
              clearOnEscape
              ListboxProps={{ style: { maxHeight: 200, overflowY: "auto" } }}
            />
          </FormControl>
        </Box>
      </Grid>
    );
  }
);
